import { Link } from 'react-router-dom';
import type { JobDetail } from '@jobos/shared/types/job';
import { formatClientName } from '@jobos/shared';
import { JobStatusBadge } from '@/components/shared/JobStatusBadge';
import { JobCardStatusBadge } from '@/components/shared/JobCardStatusBadge';
import { InvoiceStatusBadge } from '@/components/shared/InvoiceStatusBadge';
import { BillingBar } from '@/components/billing/BillingBar';
import { Button } from '@/components/ui/button';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import type { JobPhase } from './JobPhaseSelector';
import { HIGHLIGHT_COLOR_MAP } from '../utils/highlightColors';
import {
  formatInvoiceDisplay,
  getDaysSinceInvoiceSent,
} from '../utils/jobListFormatters';

interface JobListCardsProps {
  jobs: JobDetail[];
  selectedPhase: JobPhase;
  onRowClick: (jobId: string) => void;
}

export function JobListCards({ jobs, selectedPhase, onRowClick }: JobListCardsProps) {
  // The phone layout has no height cap, so the document is what scrolls here.
  useScrollRestoration({ name: 'job-list-cards', ready: jobs.length > 0, target: 'window' });

  return (
    <div className="md:hidden space-y-3">
      {jobs && jobs.length > 0 ? (
        jobs.map((job) => (
          <div
            key={job.id}
            role="button"
            tabIndex={0}
            onClick={() => onRowClick(job.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onRowClick(job.id);
              }
            }}
            // active: as well as hover: — hover never fires on a touch screen, so
            // without it a tap gives no feedback at all.
            className={`shadow rounded-lg p-4 cursor-pointer hover:shadow-md active:shadow-md transition-shadow ${job.highlightColor ? '' : 'bg-white'}`}
            style={job.highlightColor ? { backgroundColor: HIGHLIGHT_COLOR_MAP[job.highlightColor as keyof typeof HIGHLIGHT_COLOR_MAP]?.bg } : undefined}
          >
            {/* One vertical stack, not two columns. The side-by-side layout was
                inherited from the desktop table: at 390px the project type and the
                service address end up fighting for the same 150px and overlap. */}
            <div className="flex flex-wrap items-center gap-2">
              <JobStatusBadge status={job.status} noticeDate={job.noticeDate} />
              <JobCardStatusBadge status={job.jobCardStatus} />
              <InvoiceStatusBadge
                status={job.invoiceStatus}
                lastInvoicedDate={job.lastInvoicedDate}
                billingType={job.billingType}
              />
            </div>

            {/* Its own full-width line, outside the badge row: as a fourth flex
                child it would be squeezed beside the three badges. */}
            <BillingBar summary={job.jobBillingSummary} jobStatus={job.status} className="mt-2" />

            <div className="mt-2 flex items-baseline gap-2 text-xs text-gray-500">
              <span className="font-mono">{job.jobCode || '-'}</span>
              <span aria-hidden="true">·</span>
              <span className="truncate">
                {job.projectType === 'Other' && job.customProjectType
                  ? job.customProjectType
                  : job.projectType}
              </span>
            </div>

            <Link
              to={`/clients/${job.clientId}`}
              className="mt-1 block text-sm font-semibold text-gray-900 hover:text-blue-600 truncate"
              onClick={(e) => e.stopPropagation()}
            >
              {formatClientName(job.client.name, job.client.firstName, job.client.lastName)}
            </Link>

            {/* break-words, not flex-shrink-0: a long address has to be allowed to
                wrap, or it drags the card past the edge of a 390px viewport. */}
            <div className="mt-1 text-xs text-gray-600 break-words">
              <div>
                {job.location.addressLine1}
                {job.location.addressLine2 && `, ${job.location.addressLine2}`}
              </div>
              <div>{job.location.city}</div>
            </div>

            <div className="mt-1 text-xs text-gray-500">
              {selectedPhase === 'quoting' && job.noticeDate && (
                <div>Notice: {new Date(job.noticeDate).toLocaleDateString()}</div>
              )}
              {selectedPhase === 'building' && job.startDate && (
                <div>Started: {new Date(job.startDate).toLocaleDateString()}</div>
              )}
              {selectedPhase === 'billing' && (
                <>
                  {job.completedDate && (
                    <div>Completed: {new Date(job.completedDate).toLocaleDateString()}</div>
                  )}
                  {job.invoiceNumber && (
                    <div>
                      Invoice: {formatInvoiceDisplay(job.invoiceNumber, job.billingType)}{job.invoiceType === 'cash' ? ' (Cash)' : ''}
                      {getDaysSinceInvoiceSent(job.lastInvoicedDate, job.billingType, job.invoiceStatus) && (
                        <span className="text-gray-400 font-medium"> · {getDaysSinceInvoiceSent(job.lastInvoicedDate, job.billingType, job.invoiceStatus)}</span>
                      )}
                    </div>
                  )}
                </>
              )}
              {(selectedPhase === 'all' || selectedPhase === 'other') && job.description && (
                <div className="line-clamp-2">{job.description}</div>
              )}
            </div>
          </div>
        ))
      ) : (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <div className="text-gray-500">
            <p className="text-lg font-medium">No jobs found</p>
            <p className="text-sm mt-1">
              Get started by creating your first job
            </p>
            <Link to="/jobs/new" className="inline-block mt-4">
              <Button className="w-full">Create New Job</Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
